import { useState, useEffect, useCallback } from "react";
import { Download, Eye, FolderOpen, ChevronLeft, FileText, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/moneleg`;
const TOKEN_KEY = "moneleg_token";
const btn = "inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50";
const fila = "flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white text-sm";

const getToken = () => { try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; } };
const setToken = (t) => { try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY); } catch { /* sin almacenamiento */ } };

export default function MonelegPortal() {
  const [token, setTokenState] = useState(getToken());
  const [login, setLogin] = useState({ usuario: "", password: "" });
  const [error, setError] = useState("");
  const [lotes, setLotes] = useState([]);
  const [lote, setLote] = useState(null);
  const [abierto, setAbierto] = useState(null);
  const [dossier, setDossier] = useState(null);
  const [ocupado, setOcupado] = useState("");

  const salir = useCallback(() => { setToken(""); setTokenState(""); setLote(null); setDossier(null); }, []);

  const pedir = useCallback(async (ruta) => {
    const res = await fetch(`${API}${ruta}`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.status === 401) { salir(); throw new Error("Sesión caducada"); }
    if (!res.ok) throw new Error("Error");
    return res;
  }, [token, salir]);

  const descargar = async (ruta, nombre, abrir = false) => {
    setOcupado(ruta);
    try {
      const blob = await (await pedir(ruta)).blob();
      const url = URL.createObjectURL(blob);
      if (abrir) { window.open(url, "_blank"); return; }
      const a = document.createElement("a");
      a.href = url; a.download = nombre; a.click();
    } catch {
      setError("No se pudo descargar");
    } finally {
      setOcupado("");
    }
  };

  useEffect(() => {
    if (token) pedir("/lotes").then((r) => r.json()).then((d) => setLotes(d.lotes)).catch(() => {});
  }, [token, pedir]);

  const entrar = async (e) => {
    e.preventDefault();
    setError("");
    const res = await fetch(`${API}/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(login) });
    if (!res.ok) { setError("Usuario o contraseña incorrectos"); return; }
    const { token: t } = await res.json();
    setToken(t);
    setTokenState(t);
  };

  const abrirLote = async (id) => {
    setDossier(null); setAbierto(null);
    setLote(await (await pedir(`/lotes/${id}`)).json());
  };

  const abrirDossier = async (id) => {
    if (abierto === id) { setAbierto(null); return; }
    setDossier(await (await pedir(`/lotes/${lote.id}/dossiers/${id}`)).json());
    setAbierto(id);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <form onSubmit={entrar} className="w-full max-w-sm bg-white border border-slate-200 rounded-xl p-6 space-y-3">
          <h1 className="text-xl font-semibold">Moneleg · Aisla Solar</h1>
          <input className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" placeholder="Usuario" value={login.usuario} onChange={(e) => setLogin({ ...login, usuario: e.target.value })} />
          <input className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" type="password" placeholder="Contraseña" value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button className={`${btn} w-full justify-center`} type="submit">Entrar</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <h1 className="text-lg font-semibold flex-1">Moneleg · Aisla Solar</h1>
        <button className={btn} onClick={salir}>Salir</button>
      </header>
      <main className="max-w-3xl mx-auto p-4 space-y-3">
        {error && <p className="text-sm text-red-600">{error}</p>}

        {!lote && (
          <>
            <h2 className="text-base font-medium">Tus lotes</h2>
            {!lotes.length && <p className="text-sm text-slate-500">Todavía no hay lotes disponibles.</p>}
            {lotes.map((l) => (
              <button key={l.id} onClick={() => abrirLote(l.id)} className={`${fila} w-full text-left hover:bg-slate-50`}>
                <FolderOpen className="w-5 h-5 text-slate-500" />
                <span className="flex-1">{l.nombre}{l.region ? ` · ${l.region}` : ""}<span className="block text-xs text-slate-500">{l.total} expedientes</span></span>
              </button>
            ))}
          </>
        )}

        {lote && (
          <>
            <div className="flex items-center gap-2">
              <button className={btn} onClick={() => setLote(null)}><ChevronLeft className="w-4 h-4" />Lotes</button>
              <h2 className="text-base font-medium flex-1">{lote.nombre}{lote.region ? ` · ${lote.region}` : ""}</h2>
              <button className={btn} disabled={ocupado === `/lotes/${lote.id}/zip`} onClick={() => descargar(`/lotes/${lote.id}/zip`, `${lote.nombre}.zip`)}>
                {ocupado === `/lotes/${lote.id}/zip` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Descargar el lote (ZIP)
              </button>
            </div>
            {lote.dossiers.map((d) => (
              <div key={d.id} className="space-y-2">
                <div className={fila}>
                  <button className="flex-1 text-left" onClick={() => abrirDossier(d.id)}>
                    {d.reference}<span className="block text-xs text-slate-500">{d.cliente} · {d.documentos} documentos</span>
                  </button>
                  <button className={btn} disabled={ocupado === `/lotes/${lote.id}/dossiers/${d.id}/zip`} onClick={() => descargar(`/lotes/${lote.id}/dossiers/${d.id}/zip`, `${d.reference}.zip`)}>
                    <Download className="w-4 h-4" />ZIP
                  </button>
                </div>
                {abierto === d.id && dossier && (
                  <div className="ml-4 space-y-1">
                    {["documentos", "adicionales"].map((grupo) => (
                      <div key={grupo} className="space-y-1">
                        <p className="text-xs font-medium text-slate-500 pt-1">{grupo === "documentos" ? "Documentos" : "Documentos adicionales"}</p>
                        {dossier.documentos.filter((x) => x.grupo === grupo).map((x) => {
                          const ruta = `/lotes/${lote.id}/dossiers/${d.id}/doc/${x.key}`;
                          return (
                            <div key={x.key} className={fila}>
                              <FileText className="w-4 h-4 text-slate-500" />
                              <span className="flex-1">{x.label}<span className="block text-xs text-slate-500 break-all">{x.filename}</span></span>
                              <button className={btn} aria-label="Ver" onClick={() => descargar(ruta, x.filename, true)}><Eye className="w-4 h-4" /></button>
                              <button className={btn} aria-label="Descargar" disabled={ocupado === ruta} onClick={() => descargar(ruta, x.filename)}><Download className="w-4 h-4" /></button>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </main>
    </div>
  );
}
